import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Common/Header';
import { exportCatalogPdf, previewCatalog, exportCatalogExcel } from '../utils/catalogPdfExport';
import { sortProductsForList } from '../utils/productSort';
import CompanyModal from '../components/Companies/CompanyModal';
import ScreenshotCapture from '../components/Products/ScreenshotCapture';
// 需求 R152（PM 2026-09-01）：產品縮圖 hover 下載圖片小圖示。
// 下載行為與產品詳情頁共用 utils/productImageDownloader.js，見該元件檔頭。
import ProductImageDownloadButton from '../components/Products/ProductImageDownloadButton';
// 需求 R117（PM 2026-08-18 拍板 Q8=A；R3 PM 追加「四頁排列對齊客戶管理」）：
// 分頁列（含跳頁框）改用共用元件，本頁原本自寫的 getPageNumbers／.catalog-page-* 一併移除。
import Pagination from '../components/Common/Pagination';
// 需求 R106 R6：.multi-select-* 由本頁 CSS 移到共用檔，四頁（型錄／產品管理／報價單建立／編輯）共用。
import '../styles/multiSelectDropdown.css';
// 需求 R123：建立時間區間子元素樣式抽共用（新品目錄本頁沿用；報價單建立/編輯兩頁 import 同一份）。
import '../styles/dateRangeFilter.css';
import './CatalogPage.css';

const API = process.env.REACT_APP_API_URL || '/api';

const fmtPrice = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
};

// 取得預設日期區間（近一個月）
const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

// 計算兩個日期之間的月份差
const getMonthDiff = (d1, d2) => {
  const a = new Date(d1), b = new Date(d2);
  return (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth() + (b.getDate() > a.getDate() ? 0.1 : 0);
};

const COST_SOURCE_LABELS = ['工廠', '經銷1', '經銷2', '經銷3'];
const COST_SOURCE_CLS = ['cost-factory', 'cost-dealer1', 'cost-dealer2', 'cost-dealer3'];

// 需求 R16：行銷狀態用字需與「產品詳細頁」相同（PM 指示）
// 需求 R16 二次補修（2026-05-07 PM 留言）：
// 行銷狀態由「同一格上下排列」改為「左右兩個獨立欄位」，
// 表頭文字分別為「目錄狀態」與「官網狀態」，且儲存格內不再加「目錄：／官網：」前綴。
// 需求 R49（PM 2026-06-17 第四輪留言）：
// 「請將型錄管理中，目錄的『未處理』顯示方式改回原本的『-』」
// → CATALOG_STATUS_LABEL fallback 改回 '-'，配色（.mkt-status-empty）一併改回原本的灰底灰字。
// 注意：本次調整僅作用於「目錄狀態」欄；「官網狀態」欄空值仍顯示「未處理」+ 紅色系
// （改用新類別 .mkt-status-unprocessed 承接，避免與目錄欄共用 class 造成連動）。
const CATALOG_STATUS_LABEL = (v) => (v === 'in_catalog' ? '上目錄' : '-');
// 需求 R128（PM 2026-08-21 留言）：官網狀態由三選一改為四選一——
// ① 新增「官網需下架」（websiteStatus='delist_website'）
// ② 原標籤「不上行銷」改回「不上官網」（enum 值 not_on_website 不動，僅顯示字串）
// 單選限制維持不變（說明1），只是選項數 3 → 4。
const WEBSITE_STATUS_LABEL_MAP = {
  pending_website: '要上官網',
  on_website: '已上官網',
  not_on_website: '不上官網',
  delist_website: '官網需下架',
};

// 需求 R18（PM 2026-05-07 留言）：
// 型錄列表「批量修改」模式下，可編輯且儲存後會同步更新至「產品詳細頁」的欄位清單。
// 對應後端寫入端點：POST /api/products/batch-update
// （src/server/controllers/productController.js:298 batchUpdate，auditFields 內建欄位審計記錄）
// 任何新增 / 移除欄位請同時更新此清單與 productController.batchUpdate 的 auditFields。
//
// 需求 R18（PM 2026-05-07 第四次留言原文）：
// 「將『目錄狀態 / 官網狀態』可修改為可批量編輯且儲存後寫入產品詳細頁的欄位，
// 其餘未提及的欄位功能請不需做任何調整」
// → catalogStatus 與 websiteStatus 為 PM 本次明確指定之主要欄位，下方以 pmConfirmed=true 標註，
// 面板會額外顯示「PM 確認」徽章便於追蹤；其他三個欄位（productImage / englishName / salesNotes）
// 屬 需求 R16 既有功能，本單依 PM 指示「不需做任何調整」。
// 需求 R31（PM 2026-05-22 留言）：
// 型錄列表新增「葷素備註」「產品標籤」兩欄並支援批量編輯。
//
// 需求 R34（PM 2026-05-22 第四次留言原文）：
// 「取消葷素備註自由輸入功能，僅以下拉式選單選擇為主，另外將批量修改的結果回寫 Product」
// → 葷素備註 (vegetarianType) 改為 writeBack=true：和產品標籤同步寫回 Product，
// 後端 productController.batchUpdate 同步補上 'vegetarianType' 進 auditFields。
// 原本「只在前端 session 內覆寫、不動 DB」的 vegetarianOverrides / productsWithOverrides /
// selectedProductsCacheWithOverrides 派生狀態（需求 R31 三次補修引入）一併移除：
// 批量儲存後直接 fetchProducts() 重抓 DB 真值即可，不會再有「畫面顯示與 DB drift」問題。
//
// - 產品標籤 (tags)：批量編輯時可多選；儲存後寫回 Product.tags 並於 auditLog 留下變更紀錄
// （後端 productController.js batchUpdate 已加入 tags audit）。
// UI 規範：批量編輯中不顯示「管理標籤」(齒輪/OptionManager) 入口，僅作為選擇器。
const BATCH_EDITABLE_FIELDS = [
  { key: 'productImage', label: '產品圖片', detailField: 'productImage', uiHint: '點擊縮圖上傳', pmConfirmed: false, writeBack: true },
  { key: 'englishName', label: '英文品名', detailField: 'englishName', uiHint: '輸入英文品名', pmConfirmed: false, writeBack: true },
  { key: 'salesNotes', label: '業務備註', detailField: 'salesNotes', uiHint: '輸入業務備註', pmConfirmed: false, writeBack: true },
  { key: 'vegetarianType', label: '葷素備註', detailField: 'vegetarianType', uiHint: '單選：DropdownOption type=vegetarianType（需求 R34：回寫 Product.vegetarianType 並寫入 auditLog）', pmConfirmed: true, writeBack: true },
  { key: 'tags', label: '產品標籤', detailField: 'tags', uiHint: '多選：DropdownOption type=tag（寫回 Product.tags 並寫入 auditLog）', pmConfirmed: true, writeBack: true },
  { key: 'catalogStatus', label: '目錄狀態', detailField: 'catalogStatus', uiHint: '單選：上目錄／未處理', pmConfirmed: true, writeBack: true },
  { key: 'websiteStatus', label: '官網狀態', detailField: 'websiteStatus', uiHint: '單選：要上官網／已上官網／不上官網／官網需下架／未處理（需求 R128 四選一）', pmConfirmed: true, writeBack: true },
];
// 非編輯欄位（型錄列表僅顯示）：貨編 productCode、品名 productName、規格 spec、
// 箱入數 pcsPerBox、零售價 retailPrice、工廠價 factoryCost、保存期限 shelfLife、
// 目錄狀態 / 官網狀態的「未勾選列」也僅顯示。如需修改這些欄位請至產品詳細頁。
const WEBSITE_STATUS_CLS_MAP = {
  pending_website: 'mkt-status-pending',
  on_website: 'mkt-status-active',
  not_on_website: 'mkt-status-inactive',
  // 需求 R128 R1：「官網需下架」原用新色票（紫）避開既有綠／橘／藍／紅。
  // 需求 R128 R2（PM 2026-08-21 第二輪留言逐字：「官網需下架，顏色配置與
  // 『要上官網』相同。」）：class 名稱保留（各狀態仍對應各自的 class，日後要拆色不必改 JS），
  // 但 .mkt-status-delist 的三個色值已在 CatalogPage.css 改為與 .mkt-status-pending
  // （要上官網）完全相同，畫面上兩者同色、靠 pill 文字區分。
  delist_website: 'mkt-status-delist',
};

// 需求 R23：型錄篩選新增「行銷狀態」多選條件。
// 後端 GET /api/products 已支援的查詢：marketingStatus=unprocessed（catalog 與 website 皆空），
// 或 catalogStatus / websiteStatus 單值；本前端為支援「多選」行為，前端僅將使用者選取的選項
// 以單一 marketingStatuses（逗號串）送出，由 fetchProducts 端依規則決定如何套用 URL 參數。
//
// 需求 R23 二次補修（2026-05-18 PM 留言）：
// 「行銷狀態整體可複選，但『要上官網／已上官網／不上官網』三者之間維持單選機制」
// → 因此官網狀態各選項互斥，使用者切換其中一項時會自動取消其他項。
//
// 需求 R128（PM 2026-08-21，說明3「型錄管理中的篩選範圍也同步修」）：
// 官網狀態互斥組由三項擴為四項（新增「官網需下架」），互斥機制本身不變——
// WEBSITE_STATUS_VALUES 是互斥判定的唯一來源，加值即自動納入互斥。
const WEBSITE_STATUS_VALUES = ['pending_website', 'on_website', 'not_on_website', 'delist_website'];
const MARKETING_STATUS_OPTIONS = [
  { value: 'unprocessed', label: '未處理', cls: 'status-unprocessed' },
  { value: 'in_catalog', label: '上目錄', cls: 'status-catalog' },
  { value: 'pending_website', label: '要上官網', cls: 'status-to-web' },
  { value: 'on_website', label: '已上官網', cls: 'status-online' },
  { value: 'not_on_website', label: '不上官網', cls: 'status-none' },
  { value: 'delist_website', label: '官網需下架', cls: 'status-delist' },
];

// 需求 R127（PM 2026-08-21 拍板 Q1–Q7）：型錄管理「快速篩選」第三顆
// 由「 上架中產品」改為產品管理同款的「下架商品篩選器」。
// Q1=A：併入快速篩選、與「新品目錄／全部產品」維持三選一（radio 語義），不是獨立 toggle。
// Q2=A：點擊即套用（同產品管理），不必按「確定篩選」——見 handleDelistedQuickFilter。
// Q3=A：直接沿用全域 class .delisted-filter-btn（定義於 ProductsPage.css:586-618；App.js 對
// 兩頁皆靜態 import ⇒ CRA 單一 bundle、CSS 全域化，此頁可直接套用），CatalogPage.css 零改動。
// Q4：文案以「下架商品篩選器」為準（任務描述寫的「篩選期」視為錯字）；開啟後顯示
// 「 下架篩選中」比照產品管理。PM 該題以反問句回覆、未逐字拍板，故兩段文案收斂成
// 下列常數——若 PM 要改字只需改這裡一處，JSX 不必再動。
// Q5：不保留「只看上架品」入口（該顆現行送 status=active，而 Product.productStatus 的 enum
// 只有 ['listed','delisted','']，按下必定 0 筆＝既有 bug）。日後若要，正確參數是
// status=listed，另開任務處理。
const DELISTED_QUICK_FILTER = 'delisted';
const DELISTED_FILTER_LABEL_OFF = '下架商品篩選器';
const DELISTED_FILTER_LABEL_ON = '✓ 下架篩選中';

const CatalogPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  // filter options
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  // 需求 R59 D4(A)：葷素備註改純文字自由輸入，移除下拉來源；歷史值仍以 batch-update 回寫。

  // pending filter state（使用者操作中，尚未送出）
  // 需求 R71：快速篩選預設由 in_catalog 改為 all（初次載入即顯示全部產品）
  // 需求 R112：快速篩選只剩 new_recent_month／published／all 三顆——PM
  // 2026-08-18「將快速篩選的『已標記上目錄』刪除，因為與行銷狀態的功能重複」，
  // 該顆送出的參數與行銷狀態「上目錄」字元級相同（皆為 catalogStatus=in_catalog）。
  // 需求 R127（2026-08-21）：三顆的第二顆由 published 換成 delisted（下架商品篩選器），
  // 現行三值為 new_recent_month／delisted／all，'published' 已無任何引用。
  // 注意：delisted 這顆是「點擊即套用」（Q2=A），仍寫進同一支 pendingQuickFilter，
  // 只是 onClick 會同時把 applied 的 quickFilter 一起設定——見 handleDelistedQuickFilter。
  const [pendingQuickFilter, setPendingQuickFilter] = useState('all');
  const [pendingSeries, setPendingSeries] = useState([]);
  const [pendingCategories, setPendingCategories] = useState([]);
  // 需求 R112（PM 2026-08-18 拍板 Q3／Q4／Q5）：分類篩選補「關鍵字檢索」，
  // 與系列／類別同一列成為 系列－類別－關鍵字檢索 三欄（與產品管理 需求 R107 對齊）。
  // Q4=是 → 沿用本頁 pending→applied 機制（按「確定篩選」或 Enter 才送查詢，不即時搜尋）。
  const [pendingKeyword, setPendingKeyword] = useState('');
  const [pendingTags, setPendingTags] = useState([]);
  // 需求 R141（PM 2026-08-29 拍板 Q4=A「併入現有『標籤篩選』區塊，下方加第二個下拉」）：
  // 排除標籤——先以系列／類別／關鍵字／標籤篩出一批產品後，再把帶有這些標籤的產品剔除。
  // 與 pendingTags/selectedTags 一樣走 pending→applied 兩段式（AC2），選項來源沿用同一份
  // tagOptions（GET /api/products/options?type=tag），不另打 API、不新增任何標籤（AC1）。
  const [pendingExcludeTags, setPendingExcludeTags] = useState([]);
  // 需求 R23：行銷狀態（多選），值為 MARKETING_STATUS_OPTIONS.value 之陣列
  const [pendingMarketingStatuses, setPendingMarketingStatuses] = useState([]);
  const [pendingDateFrom, setPendingDateFrom] = useState(getDefaultDateRange().from);
  const [pendingDateTo, setPendingDateTo] = useState(getDefaultDateRange().to);
  const [dateError, setDateError] = useState('');

  // applied filter state（實際觸發 API 的狀態）
  // 需求 R71：與 pendingQuickFilter 同步，實際生效篩選預設 all
  const [quickFilter, setQuickFilter] = useState('all');
  const [selectedSeries, setSelectedSeries] = useState([]);
  // 需求 R112：實際送查詢的關鍵字（只有按下「確定篩選」／Enter 才會由 pendingKeyword 同步過來）
  const [keyword, setKeyword] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  // 需求 R141：實際送查詢的排除標籤（只有按下「確定篩選」才由 pendingExcludeTags 同步過來）
  const [excludeTags, setExcludeTags] = useState([]);
  const [selectedMarketingStatuses, setSelectedMarketingStatuses] = useState([]);
  const [appliedDateFrom, setAppliedDateFrom] = useState(getDefaultDateRange().from);
  const [appliedDateTo, setAppliedDateTo] = useState(getDefaultDateRange().to);

  // dropdown open/close
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  // 需求 R141：排除標籤下拉自成一顆（與包含用的標籤下拉各自開合，避免互相關閉）
  const [excludeTagsDropdownOpen, setExcludeTagsDropdownOpen] = useState(false);
  const seriesDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const tagsDropdownRef = useRef(null);
  const excludeTagsDropdownRef = useRef(null);

  // products
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // 需求 R117 Q7=A(b)：原本是 `const [limit] = useState(20)`——沒有 setter，畫面上也沒有
  // 「每頁顯示」選單，所以型錄管理永遠每頁 20 筆（8,492 筆 → 425 頁，正是 PM 附圖裡點不到
  // 中間頁碼的主因）。這裡補上 setter 與下方分頁列的選單，選項比照客戶管理 20/50/100。
  const [limit, setLimit] = useState(20);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  // 跨頁勾選快取：完整產品資料以 id 為 key 保存，
  // 確保切換分頁後仍能取得先前勾選品項的完整資料供預覽/匯出使用。
  const [selectedProductsCache, setSelectedProductsCache] = useState({});
  // 需求 R24：「全選所有商品（跨分頁）」按鈕的載入狀態，避免重複觸發大筆查詢。
  const [selectAllAcrossLoading, setSelectAllAcrossLoading] = useState(false);

  // 批量修改模式
  const [batchEditMode, setBatchEditMode] = useState(false);
  // 需求 R16（PM 2026-05-07）：
  // 批量編輯欄位擴充為 { englishName, salesNotes, productImage, catalogStatus, websiteStatus }
  // 需求 R18（PM 2026-05-07）：
  // 完整欄位清單與「儲存後同步至產品詳細頁」之說明見檔頂 BATCH_EDITABLE_FIELDS。
  const [batchEdits, setBatchEdits] = useState({});
  const [batchSaving, setBatchSaving] = useState(false);
  const batchImageInputRef = useRef(null);
  const batchImageCtxRef = useRef(null); // { productId }
  // 需求 R18（PM 2026-05-07）追加：本次批量編輯中被 × 移除的產品 id 集合。
  // 用途：在 handleSaveBatchEdit 寫入時，再做一次防呆過濾，
  // 確保 × 移除的列「絕對不會」夾帶 catalogStatus/websiteStatus 改動進入後端。
  // Why: PM 確認 × 鈕「不應修改任何產品狀態」，本 ref 為第二道保險。
  const removedDuringBatchRef = useRef(new Set());
  // 需求 R34（PM 2026-05-22 第四次留言原文）：
  // 「取消葷素備註自由輸入功能，僅以下拉式選單選擇為主，另外將批量修改的結果回寫 Product」
  //
  // 既然批量編輯結果寫回 Product.vegetarianType，前端不再需要 vegetarianOverrides / productsWithOverrides /
  // selectedProductsCacheWithOverrides 這套「只在頁面記憶體內覆寫」機制 — 直接讀 products 與
  // selectedProductsCache（fetchProducts 後就是 DB 真值）即可，避免本地覆寫與 DB drift。

  // export panel
  const [catalogTitle, setCatalogTitle] = useState('皇聚國際 - 產品型錄 2025');
const [exportFormat, setExportFormat] = useState('xlsx');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [exporting, setExporting] = useState(false);

  // company modal
  const [companyModalShow, setCompanyModalShow] = useState(false);
  const [companyModalEdit, setCompanyModalEdit] = useState(null);

  // 追加商品
  const [appendPanelOpen, setAppendPanelOpen] = useState(false);
  const [appendKeyword, setAppendKeyword] = useState('');
  const [appendResults, setAppendResults] = useState([]);
  const [appendSearching, setAppendSearching] = useState(false);
  const [appendSelected, setAppendSelected] = useState([]);
  const appendSearchRef = useRef(null);
  // 需求 R102 第七輪（PM 2026-08-03 留言：「僅需要在『已選取Ｎ項產品』有數量調整就好，
  // 不用固定顯示在每一頁，同時將『追加』兩字取消」）：與產品管理頁同步（Q3=A 兩頁行為一致）——
  // 移除 appendedProductsRef／appendedIds／「追加」標記，追加的商品只加入「按下確認追加當下那一頁」
  // 的清單，換頁或改篩選重新查詢後就回到後端回傳的純查詢結果；唯一保留的是選取計數會跟著加
  //（handleAppendConfirm 仍把追加的 _id 併入 selectedIds 與 selectedProductsCache，
  // 後者是本頁預覽／匯出的資料來源，不寫就會「計數有加、匯出卻少了這幾筆」）。

  // 需求 R130（PM 2026-08-21 拍板 Q1=A／Q2=A）：操作工具列 .catalog-action-toolbar
  // 改為吸附於共用頁首正下方（CSS 的 position: sticky; top: var(--catalog-header-height)）。
  // 這裡動態量測共用頁首 .page-header 的實際高度寫入該 CSS 變數——不可寫死 px：頁首高度會隨
  // 視窗寬度（標題／使用者區塊換行）變動，寫死就會出現重疊或縫隙。範式同 ProductDetailPage.js
  // 的 --pd-header-height。
  // Why +6px：.page-header 的 box-shadow（--shadow-medium: 0 4px 10px）會向下延伸約 14px，
  // 而 getBoundingClientRect() 不含 box-shadow；不補緩衝的話浮動工具列上緣會被頁首
  // 陰影壓住（需求 R67 時 PM 回報過同一個現象）。
  const pageRef = useRef(null);
  useLayoutEffect(() => {
    const pageEl = pageRef.current;
    if (!pageEl) return undefined;
    const headerEl = pageEl.querySelector('.page-header');
    if (!headerEl) return undefined;
    const SHADOW_BUFFER = 6;
    const apply = () => {
      const h = Math.ceil(headerEl.getBoundingClientRect().height);
      if (h > 0) pageEl.style.setProperty('--catalog-header-height', `${h + SHADOW_BUFFER}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(headerEl);
    return () => ro.disconnect();
    // 依賴 currentUser：本頁在 currentUser 尚未載入時 `return null`，掛載當下根本沒有 DOM，
    // 若用空依賴陣列，這段只會在那個「什麼都沒渲染」的時機跑一次、抓不到頁首而永遠不生效。
  }, [currentUser]);

  // close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(e.target)) {
        setSeriesDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(e.target)) {
        setTagsDropdownOpen(false);
      }
      // 需求 R141：排除標籤下拉也要跟著點外面收合（否則兩顆下拉會同時展開疊在一起）
      if (excludeTagsDropdownRef.current && !excludeTagsDropdownRef.current.contains(e.target)) {
        setExcludeTagsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close append search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (appendSearchRef.current && !appendSearchRef.current.contains(e.target)) {
        setAppendResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!token || !user._id) { navigate('/login'); return; }
    setCurrentUser(user);
  }, [navigate]);

  const fetchOptions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      // 需求 R59：vegetarianType 已改純文字輸入，不再抓取選項清單。
      const [sRes, cRes, tRes] = await Promise.all([
        fetch(`${API}/products/options?type=series`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/products/options?type=category`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/products/options?type=tag`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (sRes.ok) setSeriesOptions(await sRes.json());
      if (cRes.ok) setCategoryOptions(await cRes.json());
      if (tRes.ok) setTagOptions(await tRes.json());
    } catch (err) {
      console.error('載入選項失敗', err);
    }
  }, []);

  // 需求 R24：將「目前篩選條件」轉為 URL 參數的共用邏輯。
  // fetchProducts（分頁顯示用）與 handleSelectAllFiltered（全選跨頁用）需共用同一組篩選條件，
  // 將建構邏輯集中避免兩端 drift。呼叫端負責補上自己的 page/limit。
  // 需求 R120 已由重新立案的 需求 R131 取代（PM 2026-08-21 逐字：「此案我會
  // 依據個頁面的篩選條件設定重新立案，本任務結案」）。
  //
  // 需求 R131（PM 2026-08-21 拍板）：
  // 「型錄管理：快速篩選、行銷狀態區塊的篩選都用 AND；分類篩選區塊裡的都用 OR。」
  // Q1=A：獨立的「標籤篩選」區塊併進分類那包 OR（＝維持 需求 R106 的分組）。
  // Q2=A：行銷狀態區塊**內部**多選維持 OR；區塊與其他條件之間仍是 AND。
  // Q7＝是：關鍵字併入 OR 後，該 OR 整包仍與快速篩選（新品目錄／下架商品篩選器）、
  // 行銷狀態 AND（例：新品目錄＋關鍵字「醬」＝近一個月內、且系列/類別/標籤/關鍵字任一命中）。
  // 實作方式：後端 buildProductListFilter 以 orFields 參數化，本頁固定送出這四個欄位鍵。
  // createdFrom/createdTo（新品目錄）與 status（下架商品篩選器）**不在**清單內＝維持頂層 AND。
  const buildProductFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    // 需求 R131：本頁「分類篩選區塊」＝系列／類別／關鍵字檢索 ＋ 獨立的標籤篩選區塊（Q1=A），
    // 這四項併成同一包 OR（任一命中即列出）。字串為字面值而非共用常數，是為了讓
    // tests/catalogKeywordFilter.fixture.js 能把這支函式挖出來直接執行（sandbox 內沒有模組常數）。
    params.set('orFields', 'series,category,tags,keyword');
    if (quickFilter === 'new_recent_month') {
      params.set('createdFrom', appliedDateFrom);
      params.set('createdTo', appliedDateTo);
    } else if (quickFilter === DELISTED_QUICK_FILTER) {
      // 需求 R127（PM 2026-08-21：Q1=A／Q5=不保留「上架中產品」）：
      // 舊分支 `quickFilter === 'published' → params.set('status', 'active')` 已整支移除。
      // 移除理由（非單純換功能）：Product.productStatus 的 enum 是 ['listed','delisted','']，
      // 從來就沒有 'active' 這個值（全 src/server grep 不到），所以「上架中產品」按下去
      // 必定 0 筆，本就是壞的入口 ⇒ 移除無功能回歸風險。
      // 改送 status=delisted 走的是後端既有路徑（productController.buildProductListFilter
      // `if (status) filter.productStatus = status; else filter.productStatus = { $ne: 'delisted' }`），
      // 等於覆蓋掉「預設排除下架品」⇒ 列表只剩下架品。後端零改動。
      params.set('status', DELISTED_QUICK_FILTER);
    }
    // 'all' → no extra params
    // 需求 R112：原本這裡還有一支 `quickFilter === 'in_catalog' → catalogStatus=in_catalog`，
    // 與下方行銷狀態「上目錄」送出的參數完全相同（重複功能），依 PM 指示連同按鈕一併移除。
    // 保留的是行銷狀態選項 MARKETING_STATUS_OPTIONS 的 in_catalog 與顯示用 CATALOG_STATUS_LABEL。

    // 需求 R112（Q3／Q4）：關鍵字檢索直接沿用後端既有的 keyword 參數，
    // 檢索範圍＝貨編／中文品名／英文品名／原廠成本供應商／經銷成本供應商，任一命中即列出
    //（productController.buildProductListFilter，需求 R68 拍板）。
    // 需求 R131：keyword 自 2026-08-21 起與系列／類別／標籤同屬一包 OR（上方 orFields），
    // 不再與它們 AND——本頁語義自此與產品管理頁（全 AND）不同，兩邊靠 orFields 分岔。
    // 本函式同時被「分頁清單」與「全選所有商品（跨分頁）」共用，故關鍵字兩邊自動一致。
    if (keyword.trim()) params.set('keyword', keyword.trim());

    if (selectedSeries.length > 0) params.set('series', selectedSeries.join(','));
    if (selectedCategories.length > 0) params.set('category', selectedCategories.join(','));
    if (selectedTags.length > 0) params.set('tags', selectedTags.join(','));
    // 需求 R141（PM 2026-08-29 Q1=A／Q2=A）：排除標籤。後端 buildProductListFilter 收到
    // excludeTags 會加一條**頂層 AND** 的 { tags: { $nin: [...] } }——刻意不在 orFields 裡，
    // 所以它是「先算完上面那包 OR 再整包扣掉」，而不是 OR 群組的其中一員（進 OR 就失效）。
    // 本函式同時被「分頁清單」與「全選所有商品（跨分頁）」共用，故兩者自動一致（AC9）。
    if (excludeTags.length > 0) params.set('excludeTags', excludeTags.join(','));

    // 需求 R23：行銷狀態（多選）— 後端僅支援單值 marketingStatus/catalogStatus/websiteStatus。
    // 為相容既有 API，於只選 1 項時轉成對應單值參數；多選時優先使用 marketingStatuses（逗號串）讓後端視情況擴充。
    // 規則：unprocessed → marketingStatus=unprocessed；in_catalog → catalogStatus=in_catalog；
    // pending_website / on_website / not_on_website / delist_website → websiteStatus=該值
    // （需求 R128：官網類值走同一條 else 分支，新增狀態無須改動此處邏輯）。
    if (selectedMarketingStatuses.length === 1) {
      const v = selectedMarketingStatuses[0];
      if (v === 'unprocessed') params.set('marketingStatus', 'unprocessed');
      else if (v === 'in_catalog') params.set('catalogStatus', 'in_catalog');
      else params.set('websiteStatus', v);
    } else if (selectedMarketingStatuses.length > 1) {
      params.set('marketingStatuses', selectedMarketingStatuses.join(','));
    }
    return params;
  }, [quickFilter, appliedDateFrom, appliedDateTo, keyword, selectedSeries, selectedCategories, selectedTags, selectedMarketingStatuses, excludeTags]);

  const fetchProducts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const params = buildProductFilterParams();
    params.set('page', page);
    params.set('limit', limit);

    try {
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // 需求 R34：批量編輯結果已寫回 DB，products 直接取後端值即可。
        // 需求 R102 第七輪（PM「不用固定顯示在每一頁」）：清單與總筆數一律以後端查詢結果為準，
        // 不再補回本頁 session 追加過的商品。
        setProducts(data.products || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('載入產品失敗', err);
    }
  }, [page, limit, buildProductFilterParams]);

  const fetchCompanies = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const companies = await res.json();
        setCompanyOptions(companies);
        setSelectedCompany(prev => prev || (companies[0]?._id || ''));
      }
    } catch (err) {
      console.error('載入公司失敗', err);
    }
  }, []);

  const fetchSalesUsers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const users = await res.json();
        const sales = users.filter(u => u.isActive && (u.role === 'sales' || u.role === 'manager'));
        setSalesUsers(sales);
        if (sales.length > 0) setSelectedContactId(sales[0]._id);
      }
    } catch (err) {
      console.error('載入業務名單失敗', err);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchOptions();
      fetchSalesUsers();
      fetchCompanies();
    }
  }, [currentUser, fetchOptions, fetchSalesUsers, fetchCompanies]);

  useEffect(() => {
    if (currentUser) {
      fetchProducts();
    }
  }, [currentUser, fetchProducts]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // 選取邏輯
  const isAllSelected = products.length > 0 && products.every(p => selectedIds.includes(p._id));

  // 需求 R102 第二輪：offPageSelectedCount＝已勾選但不在目前分頁上的筆數（換頁後畫面看不到勾、
  // 計數卻照算，講明白才不會被誤讀）。第七輪起追加的商品換頁後也歸入這個計數。
  const currentPageIdSet = new Set(products.map(p => p._id));
  const offPageSelectedCount = selectedIds.filter(id => !currentPageIdSet.has(id)).length;
  const handleSelectAll = () => {
    if (isAllSelected) {
      const removingIds = new Set(products.map(p => p._id));
      setSelectedIds(prev => prev.filter(id => !removingIds.has(id)));
      setSelectedProductsCache(prev => {
        const next = { ...prev };
        removingIds.forEach(id => { delete next[id]; });
        return next;
      });
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...products.map(p => p._id)])]);
      setSelectedProductsCache(prev => {
        const next = { ...prev };
        products.forEach(p => { next[p._id] = p; });
        return next;
      });
    }
  };

  // 需求 R24：跨分頁全選——選取「目前篩選條件下」所有商品（含其他分頁）。
  // 原本 handleSelectAll 只勾選目前分頁顯示的 20 筆，使用者需逐頁點才能勾完整。
  // 本函式以同樣的篩選條件呼叫一次 /products，limit 提高以一次拿回全部 id 與必要資料，
  // 寫入 selectedIds 與 selectedProductsCache（後者供預覽/匯出沿用既有快取邏輯）。
  // 若目前已勾選的數量 ≥ total（視為已全部勾選）則改為「全部取消」。
  const handleSelectAllAcrossPages = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (total > 0 && selectedIds.length >= total) {
      setSelectedIds([]);
      setSelectedProductsCache({});
      return;
    }
    setSelectAllAcrossLoading(true);
    try {
      const params = buildProductFilterParams();
      params.set('page', 1);
      // 後端 productController.getAll 接受 limit；以一個遠大於實務目錄量的上限一次取回。
      params.set('limit', 100000);
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        alert('全選所有商品失敗，請稍後再試');
        return;
      }
      const data = await res.json();
      // 需求 R34：批量編輯結果已寫回 DB，快取直接取後端值即可。
      const all = data.products || [];
      // 需求 R102 第七輪：追加進來的商品若不符合目前篩選條件就不會出現在這次查詢結果裡，但只要它們
      // 還顯示在目前清單上（＝剛按完「確認追加」、尚未換頁／改篩選）就得一併勾選並寫入快取，
      // 否則會出現「按了全選所有商品，畫面上卻還有沒打勾的列」。改讀目前清單而非先前的 session ref。
      const allIds = new Set(all.map(p => p._id));
      const onScreenExtras = products.filter(p => !allIds.has(p._id));
      setSelectedIds([...all.map(p => p._id), ...onScreenExtras.map(p => p._id)]);
      const cache = {};
      all.forEach(p => { cache[p._id] = p; });
      onScreenExtras.forEach(p => { cache[p._id] = p; });
      setSelectedProductsCache(cache);
    } catch (err) {
      console.error('全選所有商品失敗', err);
      alert('全選所有商品失敗，請稍後再試');
    } finally {
      setSelectAllAcrossLoading(false);
    }
  };

  const handleSelectOne = (product) => {
    const id = product._id;
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
    setSelectedProductsCache(prev => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: product };
    });
  };
  // 需求 R18（PM 2026-05-07）：本按鈕僅從「本次篩選結果之列表」移除該列，
  // 不呼叫 API、不修改 websiteStatus/catalogStatus，不會改變產品在 DB 的任何狀態。
  // 用途：批量編輯時若不想對某列套用本次修改，可從清單中暫時移除（重新載入即恢復）。
  // 2026-05-07 二次確認（PM）：實測時若觀察到「目錄狀態」被改成未選擇，
  // 視為 bug；本函式必須僅進行前端 state 過濾，且 handleSaveBatchEdit 會以
  // removedDuringBatchRef 再過濾一次，杜絕任何 catalogStatus/websiteStatus 異動。
  const handleRemoveProduct = (id) => {
    removedDuringBatchRef.current.add(id);
    // 需求 R102 第七輪：追加商品不再被 fetchProducts 補回清單，因此這裡不需要再同步任何追加清單。
    setProducts(prev => prev.filter(p => p._id !== id));
    setSelectedIds(prev => prev.filter(i => i !== id));
    setSelectedProductsCache(prev => { const next = { ...prev }; delete next[id]; return next; });
    setBatchEdits(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  // 批量修改
  const handleEnterBatchEdit = () => {
    if (selectedIds.length === 0) {
      alert('請先勾選要修改的產品');
      return;
    }
    // 進入批量編輯：清空上一輪 × 移除紀錄
    removedDuringBatchRef.current = new Set();
    // 只初始化已勾選的產品（直接讀 products — DB 真值，無覆寫機制）
    const init = {};
    products.filter(p => selectedIds.includes(p._id)).forEach(p => {
      init[p._id] = {
        englishName: p.englishName || '',
        salesNotes: p.salesNotes || '',
        // 需求 R16（PM 2026-05-07）：圖片、目錄狀態、官網狀態納入批量編輯
        productImage: p.productImage || '',
        catalogStatus: p.catalogStatus || '',
        websiteStatus: p.websiteStatus || '',
        // 需求 R34（PM 2026-05-22）：葷素備註與產品標籤納入批量編輯，皆會寫回 Product
        vegetarianType: p.vegetarianType || '',
        tags: Array.isArray(p.tags) ? [...p.tags] : [],
      };
    });
    setBatchEdits(init);
    setBatchEditMode(true);
  };

  // 需求 R16：批量編輯模式下點擊縮圖呼叫 hidden file input 上傳並寫回 batchEdits
  const triggerBatchImageUpload = (productId) => {
    if (!productId) return;
    batchImageCtxRef.current = { productId };
    if (batchImageInputRef.current) {
      batchImageInputRef.current.value = '';
      batchImageInputRef.current.click();
    }
  };

  const handleBatchImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    const ctx = batchImageCtxRef.current;
    const token = localStorage.getItem('token');
    if (!file || !ctx?.productId || !token) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API}/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || '圖片上傳失敗');
      }
      const { url } = await res.json();
      setBatchEdits(prev => ({
        ...prev,
        [ctx.productId]: { ...prev[ctx.productId], productImage: url || '' },
      }));
    } catch (err) {
      alert(err.message || '圖片上傳失敗');
    } finally {
      batchImageCtxRef.current = null;
    }
  };

  const handleCancelBatchEdit = () => {
    setBatchEditMode(false);
    setBatchEdits({});
    removedDuringBatchRef.current = new Set();
  };

  const handleSaveBatchEdit = async () => {
    const token = localStorage.getItem('token');
    // 只送出有異動的品項
    // 需求 R16（PM 2026-05-07）：批量編輯需可同步寫入 productImage / catalogStatus / websiteStatus
    // 需求 R18（PM 2026-05-07 二次確認）：× 移除的列（removedDuringBatchRef）
    // 即使在 batchEdits 留有殘值，也絕不能把 catalogStatus/websiteStatus 寫入後端，
    // 避免使用者觀察到「目錄狀態被改成未選擇」之類的非預期變更。
    const removedIds = removedDuringBatchRef.current;

    const updates = Object.entries(batchEdits)
      .map(([_id, fields]) => {
        if (removedIds.has(_id)) return null; // 防呆：× 移除的列一律跳過
        const original = products.find(p => p._id === _id);
        if (!original) return null;
        const changed = {};
        if (fields.englishName !== undefined && fields.englishName !== (original.englishName || '')) {
          changed.englishName = fields.englishName;
        }
        if (fields.salesNotes !== undefined && fields.salesNotes !== (original.salesNotes || '')) {
          changed.salesNotes = fields.salesNotes;
        }
        if (fields.productImage !== undefined && fields.productImage !== (original.productImage || '')) {
          changed.productImage = fields.productImage;
        }
        if (fields.catalogStatus !== undefined && fields.catalogStatus !== (original.catalogStatus || '')) {
          changed.catalogStatus = fields.catalogStatus;
        }
        if (fields.websiteStatus !== undefined && fields.websiteStatus !== (original.websiteStatus || '')) {
          changed.websiteStatus = fields.websiteStatus;
        }
        // 需求 R31：產品標籤陣列比對（排序後 join 比較），有異動才送 API。
        // 後端 productController.js batchUpdate 對 tags 也以排序 join 判斷，並寫入 auditLog。
        if (fields.tags !== undefined) {
          const origTags = Array.isArray(original.tags) ? original.tags : [];
          const newTags = Array.isArray(fields.tags) ? fields.tags.filter(v => v != null && v !== '') : [];
          const origKey = [...origTags].map(String).sort().join('|');
          const newKey = [...newTags].map(String).sort().join('|');
          if (origKey !== newKey) {
            changed.tags = newTags;
          }
        }
        // 需求 R34（PM 2026-05-22 第四次留言）：
        // 葷素備註批量編輯結果**回寫** Product.vegetarianType（推翻 需求 R31「不回寫」決策），
        // 一併送進 batch-update API，後端 auditFields 已加入 vegetarianType，會留下 auditLog。
        if (fields.vegetarianType !== undefined
            && fields.vegetarianType !== (original.vegetarianType || '')) {
          changed.vegetarianType = fields.vegetarianType;
        }
        if (Object.keys(changed).length === 0) return null;
        return { _id, ...changed };
      })
      .filter(Boolean);

    if (updates.length === 0) {
      setBatchEditMode(false);
      setBatchEdits({});
      removedDuringBatchRef.current = new Set();
      return;
    }

    setBatchSaving(true);
    try {
      const res = await fetch(`${API}/products/batch-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.message || '儲存失敗');
        return;
      }
      // 需求 R34：批量編輯結果已寫回 DB，直接重新拉取即可取得最新真值。
      await fetchProducts();
      setBatchEditMode(false);
      setBatchEdits({});
      removedDuringBatchRef.current = new Set();
    } catch (err) {
      console.error('批量修改失敗', err);
      alert('儲存失敗');
    } finally {
      setBatchSaving(false);
    }
  };

  // 需求 R127 Q2=A：「下架商品篩選器」點擊即套用，不吃「確定篩選」。
  // ⇒ pending 與 applied 兩支必須在同一個 handler 內同步設定，否則會出現
  // 「按鈕亮起來、清單卻沒變」（產品管理 handleDelistedToggle 也是這個寫法，
  // ProductsPage.js:625-631）。
  // Q1=A 三選一：next 直接覆寫 quickFilter，等同自動取消「新品目錄／全部產品」；
  // 已是 delisted 時再按一次則回到 'all'，即 PM 驗收步驟「再點一次回到不含下架品的清單」。
  // （由 new_recent_month 切過來再切回時一律落到 'all'，不記憶前一顆，行為單純可預期。）
  // Q7 AND 語義：本 handler 刻意只動 quickFilter，不碰關鍵字／系列／類別／標籤／行銷狀態，
  // 那些已套用的條件原封不動，因此「下架」只是在既有結果上再限縮。
  const handleDelistedQuickFilter = () => {
    const next = pendingQuickFilter === DELISTED_QUICK_FILTER ? 'all' : DELISTED_QUICK_FILTER;
    setPendingQuickFilter(next);
    setQuickFilter(next);
    // 離開「新品目錄」時順手清掉日期驗證訊息，避免殘留紅字。
    setDateError('');
    setPage(1);
  };

  // 套用篩選
  const handleApplyFilters = () => {
    if (pendingQuickFilter === 'new_recent_month') {
      if (!pendingDateFrom || !pendingDateTo) {
        setDateError('請選擇起始與結束日期');
        return;
      }
      if (pendingDateFrom > pendingDateTo) {
        setDateError('起始日期不可晚於結束日期');
        return;
      }
      if (getMonthDiff(pendingDateFrom, pendingDateTo) > 2) {
        setDateError('日期區間最長為 2 個月');
        return;
      }
      setDateError('');
      setAppliedDateFrom(pendingDateFrom);
      setAppliedDateTo(pendingDateTo);
    }
    setQuickFilter(pendingQuickFilter);
    // 需求 R112 Q4=是：關鍵字與其他條件一樣走 pending→applied，按下本鈕（或輸入框 Enter）才送查詢
    setKeyword(pendingKeyword);
    setSelectedSeries(pendingSeries);
    setSelectedCategories(pendingCategories);
    setSelectedTags(pendingTags);
    // 需求 R141 AC2：排除標籤同樣是按下本鈕才生效（勾選當下不改變清單）
    setExcludeTags(pendingExcludeTags);
    setSelectedMarketingStatuses(pendingMarketingStatuses);
    // 需求 R141 Q9=A（PM 2026-08-29：「套用篩選時把『已勾選但已被排除』的產品自動取消勾選」）：
    // 本頁的預覽／匯出（Excel／PDF）吃的是 selectedIds + selectedProductsCache，**不是**當下
    // 的篩選條件。使用者若「先勾選產品、後套用排除」，被排除的產品仍會留在勾選集合裡，
    // 匯出的型錄就會包含它（R3）。故套用當下即就地清掉，讓「畫面看不到的產品不會被匯出」。
    // 判斷資料來源：跨頁勾選快取（勾選時一定會寫入）優先，後備才查目前頁面的 products；
    // 兩邊都查不到（理論上不會發生）就保守保留，寧可多留也不要無聲刪掉使用者的勾選。
    if (pendingExcludeTags.length > 0 && selectedIds.length > 0) {
      const excludeSet = new Set(pendingExcludeTags);
      const droppedIds = selectedIds.filter(id => {
        const p = selectedProductsCache[id] || products.find(x => x._id === id);
        return Array.isArray(p?.tags) && p.tags.some(t => excludeSet.has(t));
      });
      if (droppedIds.length > 0) {
        const droppedSet = new Set(droppedIds);
        setSelectedIds(prev => prev.filter(id => !droppedSet.has(id)));
        setSelectedProductsCache(prev => {
          const next = { ...prev };
          droppedIds.forEach(id => { delete next[id]; });
          return next;
        });
      }
    }
    setPage(1);
  };

  const handleResetFilters = () => {
    const defaultRange = getDefaultDateRange();
    // 需求 R71：重置後回到新預設 all，與初次載入一致
    // 需求 R127 Q6=是（PM 2026-08-21）：「清除條件」須一併關閉下架篩選。
    // 因為下架篩選就是 quickFilter === 'delisted'（Q1=A 併入快速篩選、未另開 state），
    // 下面 setPendingQuickFilter('all') + setQuickFilter('all') 兩行即已完全涵蓋——
    // 按鈕會回到未啟用的「下架商品篩選器」、查詢也不再帶 status。
    setPendingQuickFilter('all');
    // 需求 R112 Q5=是：「清除條件」一併清空關鍵字（pending 與 applied 都要清，
    // 只清 pending 會出現「輸入框空了、清單仍被舊關鍵字篩著」）
    setPendingKeyword('');
    setPendingSeries([]);
    setPendingCategories([]);
    setPendingTags([]);
    // 需求 R141 Q5=A（PM 2026-08-29：「清除條件一併清空排除標籤」＝是）：pending 與 applied
    // 兩份都要清，只清 pending 會出現「下拉空了、清單卻仍被舊排除條件扣著」（比照關鍵字）。
    setPendingExcludeTags([]);
    setPendingMarketingStatuses([]);
    setPendingDateFrom(defaultRange.from);
    setPendingDateTo(defaultRange.to);
    setDateError('');
    setQuickFilter('all');
    setKeyword('');
    setSelectedSeries([]);
    setSelectedCategories([]);
    setSelectedTags([]);
    setExcludeTags([]);
    setSelectedMarketingStatuses([]);
    setAppliedDateFrom(defaultRange.from);
    setAppliedDateTo(defaultRange.to);
    setPage(1);
  };

  // === 追加商品 handlers ===
  const handleAppendSearch = async () => {
    if (!appendKeyword.trim()) return;
    setAppendSearching(true);
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams({ keyword: appendKeyword, limit: 50 });
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAppendResults(data.products || []);
      }
    } catch (err) {
      console.error('搜尋失敗', err);
    } finally {
      setAppendSearching(false);
    }
  };

  // 需求 R102 第八輪 Q8（PM 2026-08-03 留言「Q8 維持現行」，Q3=A 兩頁一致）：
  // 自動勾選的時機維持在「按下面板下方的『確認追加』當下」，不改成按每一列的『＋ 追加』就立刻反映。
  // 因此本函式只把商品放進 appendSelected（面板內的待追加暫存），不碰 selectedIds／
  // selectedProductsCache／products／total，上方「已選取 N 項產品」在這一步不會變動。
  // 維持兩段式，就把時序寫在畫面上（本列按鈕 title＋面板下方 hint），避免使用者誤以為計數又壞了。
  const handleAppendAdd = (product) => {
    if (appendSelected.find((p) => p._id === product._id)) return;
    setAppendSelected((prev) => [...prev, product]);
  };

  const handleAppendRemove = (productId) => {
    setAppendSelected((prev) => prev.filter((p) => p._id !== productId));
  };

  // 需求 R102（PM 2026-08-03 拍板 Q3=A：型錄管理頁與產品管理頁一起修、行為一致）：
  // 原本只做 setProducts / setTotal，沒有動 selectedIds，追加進來的商品是「在清單裡但沒被勾選」，
  // 上方「已選取 N 項產品」不會變、表頭全選勾選框（isAllSelected = 本頁每筆都在 selectedIds）由已勾變未勾。
  // Q1：原拍板 C（追加前已有勾選才自動勾），PM 2026-08-03 後續留言「按了＋追加就一律勾起來」改判為 A。
  // Q7（PM 2026-08-03 第七輪「不用固定顯示在每一頁」，推翻先前的 Q4=B）：追加的商品只加入當下這一頁
  // 的清單與總筆數，換頁／改篩選重新查詢後不再出現；但 _id 仍在 selectedIds 裡，計數不會掉回去。
  const handleAppendConfirm = () => {
    // 已在目前清單裡的追加項不重複塞進清單、也不重複計入 total
    const brandNew = appendSelected.filter((ap) => !products.find((p) => p._id === ap._id));
    setProducts((prev) => {
      const newProducts = brandNew.filter((ap) => !prev.find((p) => p._id === ap._id));
      return [...prev, ...newProducts];
    });
    setTotal((prev) => prev + brandNew.length);
    // Q1=A（PM 2026-08-03 留言「按了＋追加就一律勾起來」，推翻先前的 Q1=C）：
    // 不再判斷追加前 selectedIds 是不是空的，一律把追加的商品併入勾選（0 勾選時追加 2 筆＝計數 0→2）。
    // 本頁的預覽／匯出讀的是 selectedProductsCache（跨頁勾選快取），所以自動勾選時必須同步寫入快取，
    // 否則會出現「計數有加、匯出的型錄卻少了這幾筆」。取消勾選一律仍由使用者操作 handleSelectOne。
    setSelectedIds((prev) => [...new Set([...prev, ...appendSelected.map((p) => p._id)])]);
    setSelectedProductsCache((prev) => {
      const next = { ...prev };
      appendSelected.forEach((p) => { next[p._id] = p; });
      return next;
    });
    setAppendPanelOpen(false);
    setAppendSelected([]);
    setAppendKeyword('');
    setAppendResults([]);
  };

  // 分頁
  const totalPages = Math.ceil(total / limit);
  // 需求 R117 R3：本頁原本自寫的 getPageNumbers 已移到 utils/pageJump.getPageNumbers
  // （演算法逐字相同：≤7 頁全列，否則 1 … 目前頁±1 … 末頁），由共用的 <Pagination> 呼叫。

  const getCatalogOpts = () => {
    const contact = salesUsers.find(u => u._id === selectedContactId) || {};
    const company = companyOptions.find(c => c._id === selectedCompany);
    // 跨頁勾選：依 selectedIds 順序，從快取（首選）或目前頁面 products（後備）組裝完整清單，
    // 確保切換分頁後仍能匯出/預覽其他頁勾選的品項。
    // 需求 R94（PM 2026-07-31 拍板 Q6「不會控制排序，依照設定好的排序邏輯」）：
    // selectedIds 是「勾選先後順序」——逐筆勾選 handleSelectOne 與單頁全選 handleSelectAll 都是
    // push 到陣列尾端，只有跨頁全選因直接取 API 回傳才碰巧是排序序，導致預覽／PDF／Excel 的
    // 產品順序＝使用者點選先後。此處在組裝輸出清單時統一套 productSort 排序，讓三種勾選路徑
    // （逐筆／單頁全選／跨頁全選）對同一組產品產生完全相同的順序。
    const selectedProducts = sortProductsForList(
      selectedIds
        .map(id => selectedProductsCache[id] || products.find(p => p._id === id))
        .filter(Boolean)
    );
    return {
      products: selectedProducts,
      companyInfo: {
        name: company?.name || company?.label || '皇聚食品國際有限公司',
        nameEn: company?.nameEn || '',
        address: company?.address || '',
        addressEn: company?.addressEn || '',
        tel: company?.tel || '',
        fax: company?.fax || '',
        logoUrl: company?.logoUrl || '',
      },
      title: catalogTitle,
      contact: {
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        lineId: contact.lineId || '',
      },
      filename: catalogTitle,
    };
  };

  // 預覽型錄
  const handlePreview = async () => {
    if (selectedIds.length === 0) {
      alert('請先勾選要預覽的產品');
      return;
    }
    try {
      await previewCatalog(getCatalogOpts());
    } catch (err) {
      console.error('預覽失敗', err);
    }
  };

  // 匯出型錄
  const handleExport = async () => {
    if (selectedIds.length === 0) {
      alert('請先勾選要匯出的產品');
      return;
    }
    setExporting(true);
    try {
      if (exportFormat === 'pdf') {
        await exportCatalogPdf(getCatalogOpts());
      } else {
        await exportCatalogExcel(getCatalogOpts());
      }
    } catch (err) {
      console.error('匯出失敗', err);
      alert('匯出失敗');
    } finally {
      setExporting(false);
    }
  };

  const getCostRows = (product) => {
    const costs = [];
    const fc = product.factoryCost;
    if (fc && (fc.unitPrice != null || fc.boxPrice != null)) {
      costs.push({ label: COST_SOURCE_LABELS[0], cls: COST_SOURCE_CLS[0], unitPrice: fc.unitPrice, boxPrice: fc.boxPrice });
    }
    (product.distributorCosts || []).forEach((d, i) => {
      if (d && (d.unitPrice != null || d.boxPrice != null)) {
        costs.push({ label: COST_SOURCE_LABELS[i + 1] || `經銷${i + 1}`, cls: COST_SOURCE_CLS[i + 1] || 'cost-dealer1', unitPrice: d.unitPrice, boxPrice: d.boxPrice });
      }
    });
    return costs;
  };

  // 需求 R16：列表改為「單欄全寬」，每列只顯示一個產品。
  // 移除原先 productPairs 的雙欄分組邏輯與 col-divider。

  const currentContact = salesUsers.find(u => u._id === selectedContactId);

  if (!currentUser) return null;

  return (
    /* 需求 R130：ref 供量測共用頁首高度、並把 --catalog-header-height 寫在這個根節點上
       （作用域限定本頁，不外洩到其他頁面）。 */
    <div className="catalog-page" ref={pageRef}>
      <Header
        title="型錄管理"
        user={currentUser}
        onLogout={handleLogout}
        onUserUpdate={setCurrentUser}
      />

      <div className="catalog-container">
        {/* 篩選工具列 */}
        <div className="catalog-filter-toolbar">
          {/* 快速篩選 */}
          <div className="toolbar-section">
            <div className="toolbar-section-title">快速篩選</div>
            <div className="quick-filters">
              <button
                className={`quick-filter-btn highlight${pendingQuickFilter === 'new_recent_month' ? ' active' : ''}`}
                onClick={() => setPendingQuickFilter('new_recent_month')}
              >
                <span>🆕</span> 新品目錄
              </button>
              {/* 需求 R112：「 已標記上目錄」已移除（與行銷狀態「上目錄」重複，兩者送出的
                  參數同為 catalogStatus=in_catalog）。要篩上目錄請改用下方行銷狀態的「上目錄」。 */}
              {/* 需求 R127（PM 2026-08-21）：原「 上架中產品」整顆換成「下架商品篩選器」。
                  Q1=A → 仍屬快速篩選三選一（按下即取消另兩顆）；Q2=A → 點擊即套用；
                  Q3=A → 套用全域 .delisted-filter-btn（ProductsPage.css:586-618，灰底 #e9ecef／
                         啟用中橘底 #fff3e0），刻意不再掛 .quick-filter-btn，以免兩組尺寸規則互相打架。
                  R2（PM 2026-08-21 附截圖「讓按鈕高度相同」）→ 上述全域規則把 height 寫死成
                         產品管理頁的設計稿 29.5px，比同排另兩顆矮 9.5px（實測 39px）。修正在
                         CatalogPage.css `.quick-filters .delisted-filter-btn`：height:auto +
                         上下 padding，改由 flex 交叉軸 stretch 自動同高（作用域限定在快速篩選
                         容器內，產品管理頁那顆維持 125×29.5px）。色票仍只有 ProductsPage.css 一份。
                  Q4 → 文案兩態由 DELISTED_FILTER_LABEL_OFF／_ON 兩個常數決定。 */}
              <button
                className={`delisted-filter-btn${pendingQuickFilter === DELISTED_QUICK_FILTER ? ' active' : ''}`}
                onClick={handleDelistedQuickFilter}
                aria-pressed={pendingQuickFilter === DELISTED_QUICK_FILTER}
              >
                {pendingQuickFilter === DELISTED_QUICK_FILTER ? DELISTED_FILTER_LABEL_ON : DELISTED_FILTER_LABEL_OFF}
              </button>
              <button
                className={`quick-filter-btn${pendingQuickFilter === 'all' ? ' active' : ''}`}
                onClick={() => setPendingQuickFilter('all')}
              >
                <span>📦</span> 全部產品
              </button>
            </div>
            {pendingQuickFilter === 'new_recent_month' && (
              <div className="new-product-date-range">
                <label className="catalog-filter-label">建立時間區間（最長 2 個月）</label>
                <div className="date-range-inputs">
                  <input
                    type="date"
                    className="date-input"
                    value={pendingDateFrom}
                    onChange={e => { setPendingDateFrom(e.target.value); setDateError(''); }}
                  />
                  <span className="date-range-sep">～</span>
                  <input
                    type="date"
                    className="date-input"
                    value={pendingDateTo}
                    onChange={e => { setPendingDateTo(e.target.value); setDateError(''); }}
                  />
                </div>
                {dateError && <div className="date-range-error">{dateError}</div>}
              </div>
            )}
          </div>

          {/* 分類篩選 */}
          <div className="toolbar-section">
            <div className="toolbar-section-title">分類篩選</div>
            <div className="catalog-filter-row">
              {/* 系列 */}
              <div className="catalog-filter-group">
                <label className="catalog-filter-label">系列 (可複選)</label>
                <div className="multi-select-dropdown" ref={seriesDropdownRef}>
                  <div
                    className="multi-select-trigger"
                    onClick={() => setSeriesDropdownOpen(o => !o)}
                  >
                    <div className="selected-tags">
                      {pendingSeries.length === 0
                        ? <span className="placeholder-text">全部系列</span>
                        : pendingSeries.map(s => (
                          <span key={s} className="selected-tag">
                            {s}
                            <button className="tag-remove-btn" onClick={(e) => { e.stopPropagation(); setPendingSeries(prev => prev.filter(x => x !== s)); }}>×</button>
                          </span>
                        ))
                      }
                    </div>
                    <span className="arrow">{seriesDropdownOpen ? '▲' : '▼'}</span>
                  </div>
                  {seriesDropdownOpen && (
                    <div className="multi-select-menu">
                      {seriesOptions.map(s => (
                        <label key={s._id} className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={pendingSeries.includes(s.value)}
                            onChange={() => {
                              setPendingSeries(prev =>
                                prev.includes(s.value) ? prev.filter(x => x !== s.value) : [...prev, s.value]
                              );
                            }}
                          />
                          {s.value}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 類別 */}
              <div className="catalog-filter-group">
                <label className="catalog-filter-label">類別 (可複選)</label>
                <div className="multi-select-dropdown" ref={categoryDropdownRef}>
                  <div
                    className="multi-select-trigger"
                    onClick={() => setCategoryDropdownOpen(o => !o)}
                  >
                    <div className="selected-tags">
                      {pendingCategories.length === 0
                        ? <span className="placeholder-text">全部類別</span>
                        : pendingCategories.map(c => (
                          <span key={c} className="selected-tag">
                            {c}
                            <button className="tag-remove-btn" onClick={(e) => { e.stopPropagation(); setPendingCategories(prev => prev.filter(x => x !== c)); }}>×</button>
                          </span>
                        ))
                      }
                    </div>
                    <span className="arrow">{categoryDropdownOpen ? '▲' : '▼'}</span>
                  </div>
                  {categoryDropdownOpen && (
                    <div className="multi-select-menu">
                      {categoryOptions.map(c => (
                        <label key={c._id} className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={pendingCategories.includes(c.value)}
                            onChange={() => {
                              setPendingCategories(prev =>
                                prev.includes(c.value) ? prev.filter(x => x !== c.value) : [...prev, c.value]
                              );
                            }}
                          />
                          {c.value}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 關鍵字檢索（需求 R112 Q3：與系列／類別同一列，順序 系列－類別－關鍵字檢索，
                  與產品管理頁 ProductsPage.js 一致；Q4：Enter 等同按「確定篩選」，不即時搜尋） */}
              <div className="catalog-filter-group">
                <label className="catalog-filter-label">關鍵字檢索</label>
                <input
                  type="text"
                  className="form-control catalog-keyword-input"
                  placeholder="輸入貨編／中英文名稱／供應商／條碼..."
                  value={pendingKeyword}
                  onChange={(e) => setPendingKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    // 需求 R112 R4：中文輸入法（注音／拼音）用 Enter 確認選字時，瀏覽器同樣會送出
                    // 一個 key === 'Enter' 的 keydown，且此時 isComposing 為 true。少了這道防線，
                    // 使用者打「醬」按 Enter 選字就會被當成按下「確定篩選」，帶著半成品關鍵字直接查詢
                    // ——本欄主要就是查中文品名，必須擋。keyCode 229 是舊瀏覽器組字中的相容判斷。
                    // 同頁 ProductDetailPage.js:1036/1048 已有同一道 isComposing 防線，此處對齊。
                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                    if (e.key === 'Enter') handleApplyFilters();
                  }}
                />
              </div>
            </div>
          </div>

          {/* 標籤篩選
              需求 R141（PM 2026-08-29 拍板 Q4=A）：「併入現有『標籤篩選』區塊，
              下方加第二個下拉」——故排除標籤不另開 toolbar-section，就放在本區塊內第二顆。 */}
          <div className="toolbar-section">
            <div className="toolbar-section-title">標籤篩選 (多選)</div>
            <div className="catalog-filter-label" style={{ marginBottom: '4px' }}>包含標籤（多選）</div>
            <div className="tag-dropdown-wrap" ref={tagsDropdownRef}>
              <div className={`tag-dropdown${tagsDropdownOpen ? ' open' : ''}`}>
                <div
                  className="tag-dropdown-trigger"
                  onClick={() => setTagsDropdownOpen(prev => !prev)}
                  style={{ flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '36px' }}
                >
                  {pendingTags.length === 0 ? (
                    <span className="tag-dropdown-placeholder">請選擇標籤（可複選）</span>
                  ) : (
                    pendingTags.map(tag => (
                      <span key={tag} className="tag" onClick={e => e.stopPropagation()}>
                        {tag}
                        <button className="tag-remove" onClick={e => { e.stopPropagation(); setPendingTags(prev => prev.filter(t => t !== tag)); }}>×</button>
                      </span>
                    ))
                  )}
                  <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
                </div>
                {tagsDropdownOpen && (
                  <div className="tag-dropdown-menu">
                    {tagOptions.map(opt => {
                      // 需求 R141 Q3=A（PM 2026-08-29：「UI 互斥，已在包含區勾的標籤於排除區
                      // disable，反之亦然」）：同一標籤同時被包含與排除，結果恆為 0 筆，
                      // 使用者會以為系統壞掉（R6），故從 UI 層就擋掉這個組合（AC13）。
                      const lockedByExclude = pendingExcludeTags.includes(opt.value);
                      return (
                        <label
                          key={opt._id}
                          className="tag-dropdown-item"
                          title={lockedByExclude ? '此標籤已在「排除標籤」中選取，無法同時作為包含條件' : undefined}
                          style={lockedByExclude ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                        >
                          <input
                            type="checkbox"
                            disabled={lockedByExclude}
                            checked={pendingTags.includes(opt.value)}
                            onChange={() => setPendingTags(prev =>
                              prev.includes(opt.value) ? prev.filter(t => t !== opt.value) : [...prev, opt.value]
                            )}
                          />
                          {opt.value}
                        </label>
                      );
                    })}
                    {tagOptions.length === 0 && (
                      <div style={{ padding: 'var(--spacing-sm)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        尚無標籤
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 需求 R141：排除標籤（多選）。Q1=A 頂層 AND、Q2=A 含任一即排除、Q6=A 無標籤者仍保留，
                三者的實際語義都在後端 buildProductListFilter 的 $nin，前端只負責送出 excludeTags。
                Q8=A：已選的排除標籤以 chip 顯示在觸發列即可，不另在清單上方加提示文字。 */}
            <div className="catalog-filter-label" style={{ marginTop: 'var(--spacing-sm)', marginBottom: '4px' }}>
              排除標籤（多選）
            </div>
            <div className="tag-dropdown-wrap" ref={excludeTagsDropdownRef}>
              <div className={`tag-dropdown${excludeTagsDropdownOpen ? ' open' : ''}`}>
                <div
                  className="tag-dropdown-trigger"
                  onClick={() => setExcludeTagsDropdownOpen(prev => !prev)}
                  style={{ flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '36px' }}
                >
                  {pendingExcludeTags.length === 0 ? (
                    <span className="tag-dropdown-placeholder">請選擇要排除的標籤（可複選）</span>
                  ) : (
                    pendingExcludeTags.map(tag => (
                      <span key={tag} className="tag" onClick={e => e.stopPropagation()}>
                        {tag}
                        <button className="tag-remove" onClick={e => { e.stopPropagation(); setPendingExcludeTags(prev => prev.filter(t => t !== tag)); }}>×</button>
                      </span>
                    ))
                  )}
                  <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
                </div>
                {excludeTagsDropdownOpen && (
                  <div className="tag-dropdown-menu">
                    {tagOptions.map(opt => {
                      // Q3=A 的另一半：已在「包含標籤」勾選的標籤在此 disable。
                      const lockedByInclude = pendingTags.includes(opt.value);
                      return (
                        <label
                          key={opt._id}
                          className="tag-dropdown-item"
                          title={lockedByInclude ? '此標籤已在「包含標籤」中選取，無法同時作為排除條件' : undefined}
                          style={lockedByInclude ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                        >
                          <input
                            type="checkbox"
                            disabled={lockedByInclude}
                            checked={pendingExcludeTags.includes(opt.value)}
                            onChange={() => setPendingExcludeTags(prev =>
                              prev.includes(opt.value) ? prev.filter(t => t !== opt.value) : [...prev, opt.value]
                            )}
                          />
                          {opt.value}
                        </label>
                      );
                    })}
                    {tagOptions.length === 0 && (
                      <div style={{ padding: 'var(--spacing-sm)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        尚無標籤
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 需求 R23：行銷狀態篩選（多選）— 未處理 / 上目錄 / 要上官網 / 已上官網 / 不上官網
              需求 R23 二次補修：官網狀態各項互斥，整體仍可複選。
              需求 R128（說明3）：官網狀態篩選範圍同步補上「官網需下架」，互斥組由三項變四項。 */}
          <div className="toolbar-section">
            <div className="toolbar-section-title">行銷狀態 (可複選；官網狀態四者單選)</div>
            <div className="status-filter">
              {MARKETING_STATUS_OPTIONS.map(opt => {
                const active = pendingMarketingStatuses.includes(opt.value);
                const toggle = () => {
                  setPendingMarketingStatuses(prev => {
                    if (prev.includes(opt.value)) {
                      // 取消已選項目
                      return prev.filter(v => v !== opt.value);
                    }
                    // 新增選擇：若屬於官網狀態類別，先清除同類其他項目（單選機制）
                    if (WEBSITE_STATUS_VALUES.includes(opt.value)) {
                      return [...prev.filter(v => !WEBSITE_STATUS_VALUES.includes(v)), opt.value];
                    }
                    return [...prev, opt.value];
                  });
                };
                return (
                  <div
                    key={opt.value}
                    className={`status-filter-item ${opt.cls}${active ? ' active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={toggle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                      }
                    }}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 篩選操作按鈕 */}
          <div className="filter-action-row">
            <button className="btn btn-outline btn-sm" onClick={handleResetFilters} style={{ whiteSpace: 'nowrap' }}>
              清除條件
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleApplyFilters} style={{ whiteSpace: 'nowrap', padding: 'var(--spacing-xs) var(--spacing-lg)' }}>
              確定篩選
            </button>
          </div>
        </div>

        {/* 操作工具列 */}
        <section className="catalog-action-toolbar">
          <div className="catalog-selection-info">
            {!batchEditMode && (
              <>
                <label className="catalog-select-all">
                  <input
                    type="checkbox"
                    className="catalog-checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                  全選目前頁面
                </label>
                {/* 需求 R24：跨分頁全選按鈕——勾選目前篩選條件下「全部商品」（含其他分頁），
                    保留既有「全選目前頁面」勾選框並列存在。再次點擊（已全選 total 時）等同清空勾選。 */}
                <button
                  type="button"
                  className="btn btn-outline btn-sm catalog-select-all-across"
                  onClick={handleSelectAllAcrossPages}
                  disabled={selectAllAcrossLoading || total === 0}
                  title="勾選目前篩選結果中的所有商品（包含其他分頁）"
                >
                  {selectAllAcrossLoading
                    ? '處理中...'
                    : (total > 0 && selectedIds.length >= total ? '取消全部勾選' : `全選所有商品（${total}）`)}
                </button>
              </>
            )}
            {batchEditMode
              ? (
                <span className="batch-edit-hint" title={BATCH_EDITABLE_FIELDS.map(f => `${f.label}（${f.uiHint}）→ Product.${f.detailField}`).join('；')}>
                  ✏️ 批量修改中｜可編輯欄位：{BATCH_EDITABLE_FIELDS.map(f => f.label).join('、')}（hover 此處查看欄位對應）
                </span>
              )
              : (
                /* 需求 R102 第二輪：換頁後被勾選的商品多半在別的分頁上，補上「其中 N 項不在目前頁面」。
                   第七輪（PM 2026-08-03「不用固定顯示在每一頁，同時將『追加』兩字取消」）：移除
                   「追加 N 筆固定顯示於每一頁」與其 tooltip，追加的商品換頁後統一由本計數交代，
                   與產品管理頁一致。 */
                <span
                  className="catalog-selection-count"
                  title={
                    offPageSelectedCount > 0
                      ? `目前頁面上有 ${selectedIds.length - offPageSelectedCount} 項被勾選，另有 ${offPageSelectedCount} 項在其他分頁（換頁不會取消勾選）`
                      : undefined
                  }
                >
                  已選取 <strong>{selectedIds.length}</strong> 項產品 (共 {total} 項)
                  {offPageSelectedCount > 0 && (
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                      ｜其中 {offPageSelectedCount} 項不在目前頁面
                    </span>
                  )}
                </span>
              )
            }
          </div>
          <div className="catalog-action-buttons">
            {batchEditMode ? (
              <>
                <button className="btn btn-outline btn-sm" onClick={handleCancelBatchEdit} disabled={batchSaving}>
                  取消
                </button>
                <button className="btn btn-sm btn-primary" onClick={handleSaveBatchEdit} disabled={batchSaving}>
                  {batchSaving ? '儲存中...' : '✓ 儲存修改'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-sm btn-append-trigger"
                  onClick={() => setAppendPanelOpen((prev) => !prev)}
                >
                  ＋ 追加商品
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: 'var(--color-info)', color: '#fff', border: '1px solid var(--color-info)' }}
                  onClick={handleEnterBatchEdit}
                >
                  批量修改
                </button>
                {/* 需求 R10：匯出型錄與產品頁「匯出成本表」屬同類匯出行為，統一沿用 btn-outline btn-sm 樣式（無圖示） */}
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? '匯出中...' : '匯出型錄'}
                </button>
              </>
            )}
          </div>
        </section>

        {/* ========== 追加商品面板 ========== */}
        <div className={`append-panel${appendPanelOpen ? ' open' : ''}`}>
          <div className="append-panel-header">
            <div className="append-panel-title">追加商品至目前篩選清單</div>
            <button className="append-panel-close" onClick={() => setAppendPanelOpen(false)}>×</button>
          </div>

          <div className="append-search-row">
            <div ref={appendSearchRef} className={`append-search-wrap${appendResults.length > 0 ? ' has-results' : ''}`}>
              <input
                className="append-search-input"
                type="text"
                placeholder="輸入貨編／產品名稱／條碼搜尋..."
                value={appendKeyword}
                onChange={(e) => setAppendKeyword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAppendSearch(); }}
              />
              <div className="append-search-dropdown">
                {appendResults.map((item) => {
                  const alreadySelected = appendSelected.find((p) => p._id === item._id);
                  const alreadyInList = products.find((p) => p._id === item._id);
                  return (
                    <div key={item._id} className="append-search-item">
                      <span className="append-search-item-code">{item.productCode}</span>
                      <span className="append-search-item-name">{item.productName}</span>
                      {alreadyInList || alreadySelected ? (
                        <span className="append-search-item-add already">已選取</span>
                      ) : (
                        <button
                          className="append-search-item-add"
                          /* 需求 R102 第八輪 Q8=維持現行：按這裡只是先放進下方待追加清單，
                             上方「已選取 N 項產品」要等按「確認追加」才會變動。 */
                          title="先加入下方待追加清單；按「確認追加」後才會加入目前清單並自動勾選（已選取數量一併增加）"
                          onClick={() => handleAppendAdd(item)}
                        >＋ 追加</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <button className="btn btn-sm btn-primary" onClick={handleAppendSearch} disabled={appendSearching}>
              {appendSearching ? '搜尋中...' : '搜尋'}
            </button>
          </div>

          {appendSelected.length > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
              {/* 需求 R102 第八輪 Q8=維持現行：這裡是「待追加」暫存清單，尚未計入上方選取計數。 */}
              已選取追加（{appendSelected.length} 筆，尚未計入上方選取計數）：
            </div>
          )}
          <div className="append-selected-area">
            {appendSelected.length === 0 ? (
              <span className="append-selected-empty">尚未選取任何商品</span>
            ) : (
              appendSelected.map((item) => (
                <span key={item._id} className="append-selected-chip">
                  {item.productCode} {item.productName}
                  <button className="append-selected-chip-remove" onClick={() => handleAppendRemove(item._id)}>×</button>
                </span>
              ))
            )}
          </div>

          <div className="append-panel-footer">
            {/* 需求 R102 第七輪：原文寫「以藍色標示區別」但實際上沒有這個標示（第二輪加的黃色「追加」
                標記亦已依 PM 指示移除），改成如實描述現在的行為。
                第八輪 Q8（PM「Q8 維持現行」）：補上時序「按下『確認追加』後」，明講自動勾選發生在
                確認的那一刻，而不是在面板內按每一列的「＋ 追加」時。 */}
            <span className="append-hint">按下「確認追加」後，追加的商品會加入目前清單並自動勾選（已選取數量一併增加）；換頁或改篩選後不再顯示於清單</span>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setAppendPanelOpen(false); setAppendSelected([]); setAppendResults([]); setAppendKeyword(''); }}>取消</button>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleAppendConfirm}
                disabled={appendSelected.length === 0}
              >
                確認追加 {appendSelected.length} 筆
              </button>
            </div>
          </div>
        </div>

        {/* 需求 R16：批量編輯時用於上傳產品圖片的 hidden file input */}
        <input
          ref={batchImageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleBatchImageFileChange}
        />

        {/* 產品列表表格（需求 R16：單欄全寬） */}
        <div className="catalog-table-container">
          <table className={`catalog-table catalog-table-single${batchEditMode ? ' batch-edit-active' : ''}`}>
            <thead>
              <tr>
                {!batchEditMode && <th className="text-center col-checkbox" />}
                {/* 需求 R16 補修：圖片獨立一欄（表頭不顯示欄位名稱），貨編另一欄 */}
                <th className="col-product-image" aria-label="產品圖示" />
                <th className="col-product-code">貨編<br /><span className="th-en">Item No.</span></th>
                <th>{batchEditMode ? '品名 / 英文品名' : <span>品名<br /><span className="th-en">Description</span></span>}</th>
                {batchEditMode
                  ? <th>業務備註<br /><span className="th-en">Sales Notes</span></th>
                  : <th className="text-center">規格 / 箱入數<br /><span className="th-en">Spec. / Q'ty per Carton</span></th>
                }
                {!batchEditMode && <th className="text-center">末端價 / 標準成本<br /><span className="th-en">Retail / Std. Cost</span></th>}
                {!batchEditMode && <th>保存期限 / 業務備註<br /><span className="th-en">Shelf Life / Notes</span></th>}
                {/* 需求 R31（PM 2026-05-22 留言）：
                    新增「葷素備註」「產品標籤」兩欄。順序：保存期限 → 葷素備註 → 產品標籤 → 目錄狀態 → 官網狀態。
                    一般／批量模式皆顯示；批量模式提供下拉/多選編輯 UI。
                    葷素備註批量編輯後**不寫回**產品基本資料（PM 確認，僅供單次匯出調整）；
                    產品標籤批量編輯後**寫回** Product.tags 並寫入 auditLog。 */}
                <th className="text-center col-vegetarian">葷素備註<br /><span className="th-en">Veg. Type</span></th>
                <th className="col-product-tags">產品標籤<br /><span className="th-en">Tags</span></th>
                {/* 需求 R16 二次補修：行銷狀態拆為「目錄狀態」「官網狀態」左右兩個獨立欄位 */}
                <th className="text-center col-catalog-status">目錄狀態<br /><span className="th-en">Catalog</span></th>
                <th className="text-center col-website-status">官網狀態<br /><span className="th-en">Website</span></th>
                {batchEditMode && <th className="col-remove-btn"></th>}
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  {/* 需求 R31（PM 2026-05-22 留言）：
                     新增葷素備註、產品標籤兩欄後 colSpan 再 +2
                     批量編輯模式：圖片+貨編+品名+業務備註+葷素備註+產品標籤+目錄狀態+官網狀態+移除 = 9
                     一般模式：勾選+圖片+貨編+品名+規格+價格+保存+葷素備註+產品標籤+目錄狀態+官網狀態 = 11 */}
                  <td colSpan={batchEditMode ? 9 : 11} className="catalog-empty">沒有符合條件的產品</td>
                </tr>
              ) : (
                // 需求 R34：葷素備註批量編輯改為回寫 DB，列表直接讀 products（fetchProducts 後即為 DB 真值）即可。
                products.map((product) => (
                  <tr key={product._id} className={batchEditMode ? 'catalog-row-batch' : ''}>
                    <ProductRowCells
                      product={product}
                      onRemove={handleRemoveProduct}
                      getCostRows={getCostRows}
                      fmtPrice={fmtPrice}
                      batchEditMode={batchEditMode}
                      batchEdits={batchEdits}
                      setBatchEdits={setBatchEdits}
                      selectedIds={selectedIds}
                      onToggleSelect={handleSelectOne}
                      onTriggerBatchImageUpload={triggerBatchImageUpload}
                      tagOptions={tagOptions}
                    />
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/*
            分頁
            需求 R117 R3（PM 2026-08-18）：排列對齊客戶管理——每頁顯示（左）／頁碼＋跳頁框（中）／
            筆數資訊（右）。本頁原本是整列置中、只有「每頁顯示＋上一頁／頁碼／下一頁」而且
            沒有筆數資訊，用的也是自己一套 .catalog-pagination／.catalog-page-btn 樣式，
            那正是四頁看起來最不一樣的一頁。改用共用元件後：
              · 原本「totalPages > 1 才渲染整條分頁列」的條件拿掉了——另三頁一律渲染，
                留著條件的話型錄只剩一頁時會整條消失（連「每頁顯示」都不見），排列就又不一致。
                跳頁框本身仍照 Q6=A 在只有一頁時自己不渲染。
              · 跳頁只改 page，selectedIds／selectedProductsCache 跨頁勾選一行未動（AC9）。
          */}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={limit}
            pageSizeOptions={[20, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
          />
        </div>

        {/* 匯出參數設定面板 */}
        <div className="export-panel">
          <div className="export-panel-header">
            <h2>匯出參數設定</h2>
            <span className="export-panel-subtitle">設定匯出型錄的詳細參數</span>
          </div>

          <div className="export-options">
            {/* 第一欄：目錄標題 + 聯絡人 */}
            <div className="export-option-group">
              <div className="export-top-section">
                <h3>目錄標題</h3>
                <input
                  type="text"
                  className="export-input"
                  placeholder="例如：長溫產品目錄、冷凍產品目錄"
                  value={catalogTitle}
                  onChange={e => setCatalogTitle(e.target.value)}
                />
              </div>

              <h3 className="export-section-h3">聯絡人</h3>
              <select
                className="export-select"
                value={selectedContactId}
                onChange={e => setSelectedContactId(e.target.value)}
              >
                <option value="">— 不指定聯絡人 —</option>
                {salesUsers.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.name}（{u.role === 'manager' ? '主管' : '業務'}）
                  </option>
                ))}
              </select>
              {currentContact && (
                <div className="contact-preview">
                  <div className="contact-row">
                    <span className="contact-label">聯絡人：</span>
                    <span className="contact-value">{currentContact.name}</span>
                  </div>
                  <div className="contact-row">
                    <span className="contact-label">Email：</span>
                    <span className="contact-value">{currentContact.email}</span>
                  </div>
                  {currentContact.lineId && (
                    <div className="contact-row">
                      <span className="contact-label">通訊方式：</span>
                      <span className="contact-value">{currentContact.lineId}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 第二欄：公司抬頭 + 輸出格式 */}
            <div className="export-option-group">
              <div className="export-top-section">
                <h3>公司抬頭</h3>
                <div className="company-select-row">
                  <select
                    className="export-select"
                    value={selectedCompany}
                    onChange={e => setSelectedCompany(e.target.value)}
                  >
                    {companyOptions.map(c => (
                      <option key={c._id} value={c._id}>{c.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="company-manage-btn"
                    title="編輯公司資訊"
                    onClick={() => {
                      const co = companyOptions.find(c => c._id === selectedCompany);
                      setCompanyModalEdit(co || null);
                      setCompanyModalShow(true);
                    }}
                  >&#9998;</button>
                  <button
                    type="button"
                    className="company-manage-btn"
                    title="新增公司"
                    onClick={() => {
                      setCompanyModalEdit(null);
                      setCompanyModalShow(true);
                    }}
                  >+</button>
                </div>
              </div>

              <h3 className="export-section-h3">輸出格式</h3>
              <select
                className="export-select"
                value={exportFormat}
                onChange={e => setExportFormat(e.target.value)}
              >
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </div>

          <div className="export-actions">
            <button className="btn btn-outline btn-sm" onClick={handlePreview}>預覽</button>
            <button
              className="btn btn-sm btn-success"
              onClick={handleExport}
              disabled={exporting}
            >
              <span>📥</span> {exporting ? '匯出中...' : '立即匯出'}
            </button>
          </div>
        </div>
      </div>

      <CompanyModal
        show={companyModalShow}
        onClose={() => setCompanyModalShow(false)}
        onSaved={() => fetchCompanies()}
        company={companyModalEdit}
        isEdit={!!companyModalEdit}
      />

    </div>
  );
};

// 需求 R16 補修：圖片與貨編改為兩個獨立欄位（圖片在第一欄，貨編在第二欄）
const ProductImageOnlyCell = ({ product }) => (
  /* 需求 R152（PM 2026-09-01「型錄管理…滑鼠移到產品縮圖時出現下載圖片按鈕」）：
     本元件同時服務型錄的一般列與批量編輯模式下「未勾選」的列（CatalogPage.js:1973／:2103 兩處
     呼叫），是型錄頁最主要的縮圖 —— 下載入口放這裡才是使用者真正 hover 的那張圖，
     且與截圖鈕不同，**不綁批量編輯模式**（下載是唯讀行為）。
     原本是裸 <img>，沒有定位容器，故補一層 .product-image-download-wrap；
     該 class 是 line-height:0 的 inline-block＋vertical-align:middle，
     維持 .catalog-product-thumb 既有的 56×56 與垂直置中外觀。 */
  product.productImage ? (
    <span className="product-image-download-wrap product-image-download-host">
      <img className="catalog-product-thumb" src={product.productImage} alt={product.productName || ''} />
      <ProductImageDownloadButton src={product.productImage} productName={product.productName} />
    </span>
  ) : (
    <span className="catalog-product-thumb-empty">無圖片</span>
  )
);

// 需求 R16 二次補修：行銷狀態由「同一格上下排列」改為「左右兩個獨立欄位」
// 表頭已在外層分別標示「目錄狀態」「官網狀態」，因此儲存格內不再加 prefix。
const CatalogStatusPill = ({ product }) => {
  const catalogVal = product.catalogStatus;
  const catalogActive = catalogVal === 'in_catalog';
  const catalogLabel = CATALOG_STATUS_LABEL(catalogVal);
  return (
    <span className={`mkt-status-pill ${catalogActive ? 'mkt-status-active' : 'mkt-status-empty'}`}>
      {catalogLabel}
    </span>
  );
};

const WebsiteStatusPill = ({ product }) => {
  const websiteVal = product.websiteStatus;
  // 需求 R49：websiteStatus 為空/未設定時，顯示「未處理」（紅色系）。
  // 第四輪 PM 留言只調整「目錄狀態」欄文字回 '-'+灰色，「官網狀態」未處理仍維持紅色，
  // 故 fallback class 改用獨立的 .mkt-status-unprocessed，與目錄欄使用的 .mkt-status-empty 解耦。
  const websiteLabel = WEBSITE_STATUS_LABEL_MAP[websiteVal] || '未處理';
  const websiteCls = WEBSITE_STATUS_CLS_MAP[websiteVal] || 'mkt-status-unprocessed';
  return (
    <span className={`mkt-status-pill ${websiteCls}`}>
      {websiteLabel}
    </span>
  );
};

// 需求 R31（PM 2026-05-22 留言）：列表顯示用元件 — 葷素備註 & 產品標籤
// 葷素備註：純文字顯示；無值顯示 '—'
const VegetarianTypeDisplay = ({ product }) => (
  <span className="catalog-veg-text">{product.vegetarianType || '—'}</span>
);

// 產品標籤：陣列；以 chip 呈現；無值顯示 '—'
const ProductTagsDisplay = ({ product }) => {
  const tags = Array.isArray(product.tags) ? product.tags.filter(Boolean) : [];
  if (tags.length === 0) return <span className="catalog-tags-empty">—</span>;
  return (
    <div className="catalog-tags-chips">
      {tags.map((t) => (
        <span key={t} className="catalog-tag-chip">{t}</span>
      ))}
    </div>
  );
};

// 需求 R31：批量編輯模式下「產品標籤」inline 多選下拉
// 規範：UI 限制 — 不顯示「管理標籤」(齒輪/OptionManager) 入口，僅作為選擇器。
// 內聚 open/close state，每列獨立一個下拉；點外面自動收起。
const BatchTagsEditor = ({ value, onChange, options }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div className="tag-dropdown-wrap" ref={ref}>
      <div className={`tag-dropdown${open ? ' open' : ''}`}>
        <div
          className="tag-dropdown-trigger form-control"
          style={{ flexWrap: 'wrap', gap: 4, minHeight: 32, height: 'auto', padding: '2px 6px' }}
          onClick={() => setOpen((p) => !p)}
        >
          {selected.length === 0 ? (
            <span className="tag-dropdown-placeholder">請選擇標籤（可複選）</span>
          ) : (
            selected.map((t) => (
              <span key={t} className="tag" onClick={(e) => e.stopPropagation()} style={{ margin: 0 }}>
                {t}
                <button
                  className="tag-remove"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(t); }}
                >×</button>
              </span>
            ))
          )}
          <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
        </div>
        {open && (
          <div className="tag-dropdown-menu">
            {options.length === 0 ? (
              <div className="tag-dropdown-item" style={{ color: 'var(--color-text-secondary)' }}>無可選標籤</div>
            ) : (
              options.map((opt) => (
                <label key={opt._id || opt.value} className="tag-dropdown-item">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                  />
                  {opt.value}
                </label>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// 單一產品列的 td 群組（需求 R16：單欄全寬版）
// 需求 R102 第七輪（PM 2026-08-03「將『追加』兩字取消」）：移除 isAppended prop 與「追加」標記。
const ProductRowCells = ({ product, onRemove, getCostRows, fmtPrice, batchEditMode, batchEdits, setBatchEdits, selectedIds, onToggleSelect, onTriggerBatchImageUpload, tagOptions = [] }) => {
  const editData = batchEdits?.[product._id] || {};

  const handleBatchField = (field, value) => {
    setBatchEdits(prev => ({
      ...prev,
      [product._id]: { ...prev[product._id], [field]: value },
    }));
  };

  if (batchEditMode) {
    const isSelected = selectedIds?.includes(product._id);
    // 需求 R16（PM 2026-05-07）：批量編輯模式下圖片可點擊上傳，
    // 目錄狀態 / 官網狀態改為下拉式單選控制元件並寫回 Product。
    // 需求 R31（PM 2026-05-22）：新增葷素備註下拉（不寫回）與產品標籤多選（寫回）。
    const editingImage = editData.productImage !== undefined ? editData.productImage : (product.productImage || '');
    const editingCatalog = editData.catalogStatus !== undefined ? editData.catalogStatus : (product.catalogStatus || '');
    const editingWebsite = editData.websiteStatus !== undefined ? editData.websiteStatus : (product.websiteStatus || '');
    const editingVeg = editData.vegetarianType !== undefined ? editData.vegetarianType : (product.vegetarianType || '');
    const editingTags = Array.isArray(editData.tags)
      ? editData.tags
      : (Array.isArray(product.tags) ? product.tags : []);
    return (
      <>
        {/* 圖片：批量編輯模式且勾選 → 點擊縮圖更換圖片
            需求 R18（PM 2026-05-07 留言）：
            滑鼠 hover 縮圖時，需顯示「更換圖片」遮罩文字，
            行為與產品列表管理（ProductsPage）批量編輯模式一致。 */}
        <td className="catalog-product-image-cell" style={!isSelected ? { opacity: 0.4 } : undefined}>
          {isSelected ? (
            <div
              className="catalog-product-thumb-edit-wrap product-image-download-host"
              onClick={() => onTriggerBatchImageUpload && onTriggerBatchImageUpload(product._id)}
              title="點擊更換產品圖片"
            >
              {editingImage ? (
                <img className="catalog-product-thumb" src={editingImage} alt={product.productName || ''} />
              ) : (
                <span className="catalog-product-thumb-empty">點擊上傳</span>
              )}
              <span className="catalog-product-thumb-edit-overlay">更換圖片</span>
              {/* 需求 R88：圖片框左下角截圖小圖示（絕對定位、不佔版面）。
                  compact 版按鈕自帶 stopPropagation，不會觸發外層縮圖的檔案上傳；
                  不支援 getDisplayMedia 的瀏覽器 ScreenshotCapture 回傳 null，不渲染。 */}
              <span
                className="catalog-product-thumb-screenshot-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <ScreenshotCapture
                  compact
                  onUploaded={(url) => handleBatchField('productImage', url || '')}
                />
              </span>
              {/* 需求 R152：批量編輯且已勾選的那一列，圖片框顯示的是 editingImage（可能是剛截圖
                  尚未存檔的那張），下載來源因此用 editingImage 而不是 product.productImage。
                  元件自帶 stopPropagation，不會觸發外層的「點擊更換產品圖片」上傳。 */}
              <ProductImageDownloadButton src={editingImage} productName={product.productName} />
            </div>
          ) : (
            <ProductImageOnlyCell product={product} />
          )}
        </td>
        <td className="catalog-code col-product-code" style={!isSelected ? { opacity: 0.4 } : undefined}>
          <span className="catalog-code-text">{product.productCode || '—'}</span>
        </td>
        <td style={!isSelected ? { opacity: 0.4 } : undefined}>
          <div className="cell-stack">
            <span className="cell-main">{product.productName || '—'}</span>
            {isSelected ? (
              <input
                className="batch-edit-input"
                value={editData.englishName ?? ''}
                onChange={e => handleBatchField('englishName', e.target.value)}
                placeholder="輸入英文品名..."
              />
            ) : (
              <span className="product-name-sub">{product.englishName || ''}</span>
            )}
          </div>
        </td>
        <td style={!isSelected ? { opacity: 0.4 } : undefined}>
          {isSelected ? (
            <input
              className="batch-edit-input"
              value={editData.salesNotes ?? ''}
              onChange={e => handleBatchField('salesNotes', e.target.value)}
              placeholder="輸入業務備註..."
            />
          ) : (
            <span className="product-name-sub">{product.salesNotes || ''}</span>
          )}
        </td>
        {/* 需求 R59 D4(A)：葷素備註 — 批量編輯改純文字自由輸入（推翻 需求 R34 拍板方向）。
            仍保留 handleSaveBatchEdit 的回寫邏輯：批量修改結果送入 batch-update API 並寫 auditLog。
            PM 2026-07-04 已理解「純 text 可能重複輸入相同錯字」的權衡。 */}
        <td className="text-center col-vegetarian" style={!isSelected ? { opacity: 0.4 } : undefined}>
          {isSelected ? (
            <input
              type="text"
              className="batch-edit-input"
              value={editingVeg}
              placeholder="葷素備註"
              onChange={(e) => handleBatchField('vegetarianType', e.target.value)}
              title="批量修改結果會回寫 Product.vegetarianType 並寫入 auditLog"
            />
          ) : (
            <VegetarianTypeDisplay product={product} />
          )}
        </td>
        {/* 需求 R31（PM 2026-05-22 留言）：產品標籤 — 多選編輯，**寫回** Product.tags
            UI 規範：批量編輯中不顯示「管理標籤」(齒輪/OptionManager) 入口，僅作為選擇器。
            後端 productController.js batchUpdate auditFields 已加入 tags，寫入後同步 auditLog。 */}
        <td className="col-product-tags" style={!isSelected ? { opacity: 0.4 } : undefined}>
          {isSelected ? (
            <BatchTagsEditor
              value={editingTags}
              onChange={(arr) => handleBatchField('tags', arr)}
              options={tagOptions}
            />
          ) : (
            <ProductTagsDisplay product={product} />
          )}
        </td>
        {/* 目錄狀態：上目錄 / 未設定（單選）
            需求 R18（PM 2026-05-07 留言）：
            目錄狀態修改選項僅保留「上目錄」與「未設定」，移除「不上目錄」選項。
            （資料模型 Product.catalogStatus 仍保留 'not_in_catalog' enum 供既有資料相容顯示，
             但批量編輯不再產生此值；如需「不上目錄」請至產品詳細頁設定。） */}
        <td className="text-center col-catalog-status" style={!isSelected ? { opacity: 0.4 } : undefined}>
          {isSelected ? (
            <select
              className="batch-edit-input"
              value={editingCatalog}
              onChange={e => handleBatchField('catalogStatus', e.target.value)}
            >
              <option value="">— 未處理 —</option>
              <option value="in_catalog">上目錄</option>
            </select>
          ) : (
            <CatalogStatusPill product={product} />
          )}
        </td>
        {/* 官網狀態：要上官網 / 已上官網 / 不上官網 / 官網需下架
            （單選，PM 指示「不得複選」；需求 R128 由三選一改四選一，單選限制不變） */}
        <td className="text-center col-website-status" style={!isSelected ? { opacity: 0.4 } : undefined}>
          {isSelected ? (
            <select
              className="batch-edit-input"
              value={editingWebsite}
              onChange={e => handleBatchField('websiteStatus', e.target.value)}
            >
              <option value="">— 未處理 —</option>
              <option value="pending_website">要上官網</option>
              <option value="on_website">已上官網</option>
              <option value="not_on_website">不上官網</option>
              <option value="delist_website">官網需下架</option>
            </select>
          ) : (
            <WebsiteStatusPill product={product} />
          )}
        </td>
        <td className="col-remove-btn">
          {isSelected && (
            <button
              className="catalog-btn-remove"
              title="從本次列表移除（不會更動產品狀態）"
              aria-label="從本次列表移除（不會更動產品狀態）"
              onClick={() => onRemove(product._id)}
            >×</button>
          )}
        </td>
      </>
    );
  }

  const isSelected = selectedIds?.includes(product._id);
  const selCls = isSelected ? ' catalog-cell-selected' : '';
  return (
    <>
      <td className={`text-center col-checkbox${selCls}`}>
        <input
          type="checkbox"
          className="catalog-checkbox"
          checked={!!isSelected}
          onChange={() => onToggleSelect(product)}
        />
      </td>
      {/* 需求 R16 補修：圖片獨立第一欄、貨編獨立第二欄 */}
      <td className={`catalog-product-image-cell${selCls}`}>
        <ProductImageOnlyCell product={product} />
      </td>
      <td className={`catalog-code col-product-code${selCls}`}>
        <span className="catalog-code-text">{product.productCode || '—'}</span>
      </td>
      <td className={selCls || undefined}>
        <div className="cell-stack">
          <span className="cell-main">{product.productName || '—'}</span>
          {product.englishName
            ? <span className="product-name-sub">{product.englishName}</span>
            : <span className="product-name-sub missing">英文名稱未填寫</span>
          }
        </div>
      </td>
      <td className={`text-center${selCls}`}>
        <div className="cell-stack">
          <span className="cell-main">{product.spec || '—'}</span>
          {product.pcsPerBox != null && <span className="cell-sub">{product.pcsPerBox}入/箱</span>}
        </div>
      </td>
      <td className={`text-center${selCls}`}>
        <div className="cell-stack">
          <span className="cell-main">{product.retailPrice != null ? fmtPrice(product.retailPrice) : '—'}</span>
          <span className="cell-sub">
            {(() => {
              const fc = product.factoryCost;
              const v = fc ? (fc.boxPrice ?? fc.unitPrice) : null;
              return v != null ? fmtPrice(v) : '—';
            })()}
          </span>
        </div>
      </td>
      <td className={selCls || undefined}>
        <div className="cell-stack">
          <span className="cell-main">{product.shelfLife || '—'}</span>
          {product.salesNotes && (
            <span className="product-name-sub">{product.salesNotes}</span>
          )}
        </div>
      </td>
      {/* 需求 R31（PM 2026-05-22 留言）：
          一般模式顯示葷素備註與產品標籤；無值顯示 '—'。 */}
      <td className={`text-center col-vegetarian${selCls}`}>
        <VegetarianTypeDisplay product={product} />
      </td>
      <td className={`col-product-tags${selCls}`}>
        <ProductTagsDisplay product={product} />
      </td>
      <td className={`text-center col-catalog-status${selCls}`}>
        <CatalogStatusPill product={product} />
      </td>
      <td className={`text-center col-website-status${selCls}`}>
        <WebsiteStatusPill product={product} />
      </td>
    </>
  );
};

export default CatalogPage;
